import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, Car, ChevronDown, CheckCircle2 } from 'lucide-react';

interface MitarbeiterOption {
  id: string;
  vorname: string;
  nachname: string;
}

export interface DemontageData {
  demontageNoetig: boolean;
  verkehrssicherheitMitarbeiterId: string;
  verkehrssicherheitMitarbeiterName: string;
  ersatzfahrzeugGewaehlt: boolean;
}

interface Props {
  onComplete: (data: DemontageData) => void;
  onBack: () => void;
}

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';

export function buildDemontageResult(
  demontageNoetig: boolean,
  mitarbeiterId: string,
  mitarbeiterName: string,
  ersatzfahrzeugGewaehlt: boolean,
): DemontageData {
  return {
    demontageNoetig,
    verkehrssicherheitMitarbeiterId: mitarbeiterId,
    verkehrssicherheitMitarbeiterName: mitarbeiterName,
    ersatzfahrzeugGewaehlt: demontageNoetig ? ersatzfahrzeugGewaehlt : false,
  };
}

export function DemontageStep({ onComplete, onBack }: Props) {
  const [demontageNoetig, setDemontageNoetig] = useState<boolean | null>(null);
  const [mitarbeiterListe, setMitarbeiterListe] = useState<MitarbeiterOption[]>([]);
  const [mitarbeiterId, setMitarbeiterId] = useState('');
  const [mitarbeiterName, setMitarbeiterName] = useState('');
  const [ersatzfahrzeugGewaehlt, setErsatzfahrzeugGewaehlt] = useState(false);
  const [ladeStatus, setLadeStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  useEffect(() => {
    if (demontageNoetig !== true) return;
    setLadeStatus('loading');
    fetch('/api/kiosk/mitarbeiter-aktiv')
      .then(r => r.json())
      .then((data: MitarbeiterOption[]) => {
        setMitarbeiterListe(data);
        if (data.length > 0 && !mitarbeiterId) {
          setMitarbeiterId(data[0].id);
          setMitarbeiterName(`${data[0].vorname} ${data[0].nachname}`);
        }
        setLadeStatus('idle');
      })
      .catch(() => setLadeStatus('error'));
  }, [demontageNoetig]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNein = () => {
    onComplete(buildDemontageResult(false, '', '', false));
  };

  const handleJa = () => {
    setDemontageNoetig(true);
  };

  const handleMitarbeiterChange = (id: string) => {
    setMitarbeiterId(id);
    const m = mitarbeiterListe.find(x => x.id === id);
    setMitarbeiterName(m ? `${m.vorname} ${m.nachname}` : '');
  };

  const canProceedWeiter = mitarbeiterId !== '' || mitarbeiterName.trim() !== '';

  const handleWeiter = () => {
    if (!canProceedWeiter) return;
    onComplete(buildDemontageResult(true, mitarbeiterId, mitarbeiterName, ersatzfahrzeugGewaehlt));
  };

  return (
    <div data-testid="demontage-step" className="space-y-6">
      <div className="text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
          style={{ background: BRAND_BLUE }}
        >
          <Wrench size={26} className="text-white" />
        </div>
        <h2 className="text-[22px] font-bold text-gray-900">Demontage / Vermessung</h2>
        <p className="text-[14px] text-gray-500 mt-1 max-w-xs mx-auto leading-relaxed">
          Falls Ihr Fahrzeug zur genauen Begutachtung zerlegt werden muss
        </p>
      </div>

      {demontageNoetig === null && (
        <motion.div
          key="auswahl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <p className="text-[15px] font-semibold text-gray-700 text-center mb-4">
            Ist eine Demontage / Vermessung nötig?
          </p>

          <button
            data-testid="demontage-nein"
            onClick={handleNein}
            className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200 bg-white transition-all hover:border-gray-300 hover:bg-gray-50 text-left"
            style={{ minHeight: 72 }}
          >
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 size={22} className="text-gray-400" />
            </div>
            <div>
              <span className="text-[16px] font-bold text-gray-800 block">Nein</span>
              <span className="text-[13px] text-gray-500">Kein Zerlegen nötig — direkt weiter</span>
            </div>
          </button>

          <button
            data-testid="demontage-ja"
            onClick={handleJa}
            className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left"
            style={{
              minHeight: 72,
              borderColor: BRAND_BLUE,
              background: '#EFF6FF',
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: BRAND_BLUE }}
            >
              <Wrench size={22} className="text-white" />
            </div>
            <div>
              <span className="text-[16px] font-bold text-gray-800 block">Ja</span>
              <span className="text-[13px] text-gray-500">Fahrzeug muss zerlegt / vermessen werden</span>
            </div>
          </button>

          <div className="pt-2">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
              style={{ minHeight: 52 }}
            >
              Zurück
            </button>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {demontageNoetig === true && (
          <motion.div
            key="details"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-5"
          >
            {/* Verkehrssicherheit-Dropdown */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-5 shadow-sm space-y-3">
              <div className="flex items-center gap-3 mb-1">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: BRAND_BLUE }}
                >
                  <Wrench size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-gray-900">Verkehrssicherheit</h3>
                  <p className="text-[11px] text-gray-500">Wer übernimmt die Verantwortung während der Vermessung?</p>
                </div>
              </div>

              {ladeStatus === 'loading' && (
                <div className="flex items-center gap-2 py-2 text-[13px] text-gray-500">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full"
                  />
                  Mitarbeiter werden geladen…
                </div>
              )}

              {ladeStatus === 'error' && (
                <div className="space-y-2">
                  <p className="text-[13px] text-red-500">
                    Mitarbeiter konnten nicht geladen werden. Bitte Mitarbeiternamen eingeben.
                  </p>
                  <input
                    data-testid="mitarbeiter-name-fallback"
                    type="text"
                    value={mitarbeiterName}
                    onChange={e => { setMitarbeiterName(e.target.value); setMitarbeiterId(''); }}
                    placeholder="Name des Mitarbeiters"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#1E5FB7]/30 focus:border-[#1E5FB7] transition-all"
                    style={{ minHeight: 48 }}
                  />
                </div>
              )}

              {ladeStatus === 'idle' && mitarbeiterListe.length > 0 && (
                <div className="relative">
                  <select
                    data-testid="verkehrssicherheit-dropdown"
                    value={mitarbeiterId}
                    onChange={e => handleMitarbeiterChange(e.target.value)}
                    className="w-full appearance-none px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 bg-white text-[15px] font-medium text-gray-800 focus:outline-none focus:ring-2 focus:border-[#1E5FB7] transition-all"
                    style={{ minHeight: 52 }}
                  >
                    {mitarbeiterListe.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.vorname} {m.nachname}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              )}
            </div>

            {/* Hinweis Ersatzfahrzeug */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <p className="text-[13px] text-blue-700 leading-relaxed">
                Während der Vermessung erhalten Sie ein Ersatzfahrzeug von MietGo — kostenfrei.
              </p>
            </div>

            {/* Ersatzfahrzeug-Toggle */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: BRAND_GREEN }}
                >
                  <Car size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-gray-900">Ersatzfahrzeug reservieren?</h3>
                  <p className="text-[11px] text-gray-500">Pool-Fahrzeug für die Demontage-Dauer</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="ersatzfahrzeug-ja"
                  onClick={() => setErsatzfahrzeugGewaehlt(true)}
                  className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold text-[14px]"
                  style={{
                    minHeight: 64,
                    borderColor: ersatzfahrzeugGewaehlt ? BRAND_GREEN : '#E5E7EB',
                    background: ersatzfahrzeugGewaehlt ? '#ECFDF5' : '#F9FAFB',
                    color: ersatzfahrzeugGewaehlt ? '#065F46' : '#374151',
                  }}
                >
                  {ersatzfahrzeugGewaehlt && <CheckCircle2 size={16} className="text-emerald-600" />}
                  Ja, bitte
                </button>

                <button
                  data-testid="ersatzfahrzeug-nein"
                  onClick={() => setErsatzfahrzeugGewaehlt(false)}
                  className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold text-[14px]"
                  style={{
                    minHeight: 64,
                    borderColor: !ersatzfahrzeugGewaehlt ? '#6B7280' : '#E5E7EB',
                    background: !ersatzfahrzeugGewaehlt ? '#F9FAFB' : 'white',
                    color: '#374151',
                  }}
                >
                  Nein danke
                </button>
              </div>

              {ersatzfahrzeugGewaehlt && (
                <p className="mt-3 text-[12px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                  Ein Pool-Fahrzeug wird für Sie reserviert.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setDemontageNoetig(null)}
                className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 52 }}
              >
                Zurück
              </button>

              <button
                data-testid="demontage-weiter"
                onClick={handleWeiter}
                disabled={!canProceedWeiter}
                className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
              >
                Weiter
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
